import useStyles from './AdditionalDetailsEditableStyles';
import useStylesCommon from '../common/AdditionalDetailsCommonStyles';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../../const/i18nNamespaces';
import {Box, Button, TextField, Typography} from '@material-ui/core';
import {Appointment, AppointmentGroupEnum} from '../../../../types/appointments';
import {useContext, useEffect, useState} from 'react';
import Dropzone from '../Dropzone/Dropzone';
import {
    fetchAppointmentDetails,
    setAdditionalPatientDetails,
} from '../../../../store/appointmentDetailsSlice';
import {useAppDispatch} from '../../../../hooks/customReduxHooks';
import {Loading} from '../../../index';
import {isObject} from 'lodash-es';
import {refineString, stringValuesDiffers} from '../../../../utils/string';
import appointmentsProvider from '../../../../services/appointmentsProvider';
import PatientAppointmentSurveyPrompt from '../PatientAppointmentSurveyPrompt/PatientAppointmentSurveyPrompt';
import { appointmentsApi } from '../../../../api/appointments/appointmentsApi';
import { GRAY } from '../../../../const/colors';
import PatientPrescriptionFormPrompt from '../PatientPrescriptionFormPrompt/PatientPrescriptionFormPrompt';
import prescriptionsApi from '../../../../api/prescriptions/prescriptionsApi';
import patientProvider from '../../../../services/patientProvider';
import IdentityConfirmationPrompt from '../IdentityConfirmationPrompt/IdentityConfirmationPrompt';
import { AlertContext } from '../../../../contexts/AlertContext';

interface Props {
    appointment: Appointment;
}

const AdditionalDetailsEditable = ({appointment}: Props) => {
    const openAlert = useContext(AlertContext);
    const [reasonsInputValue, setReasonsInputValue] = useState(
        appointment.patientReasons,
    );
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const {t} = useTranslation(i18nNamespaces.APPOINTMENT_DETAILS);
    const classes = useStyles();
    const classesCommon = useStylesCommon();
    const isPrescriptionAppointment = appointment.appointmentGroup === AppointmentGroupEnum.PrescriptionAppointment; 

    const saveChangesBtnDisabled =
        !isObject(appointment) ||
        !stringValuesDiffers(appointment.patientReasons, reasonsInputValue);

    const [checkingSurvey, setCheckingSurvey] = useState<boolean>(true);
    const [surveyRequired, setSurveyRequired] = useState<boolean>(true);

    const [checkingPrescriptionDraft, setCheckingPrescriptionDraft] = useState<boolean>(true);
    const [prescriptionDraftExists, setPrescriptionDraftExists] = useState<boolean>(false);
    
    const [checkingIdentityConfirmation, setCheckingIdentityConfirmation] = useState<boolean>(true);
    const [identityConfirmed, setIdentityConfirmed] = useState<boolean>(false);

    useEffect(() => {
        fetchIdentityConfirmationStatus();
    }, []);

    useEffect(() => {
        setCheckingPrescriptionDraft(true);
        prescriptionsApi
            .getPrescriptionSetDraftExists(parseInt(appointment.id))
            .then(response => setPrescriptionDraftExists(response.data.result))
            .catch(() => setPrescriptionDraftExists(false))
            .finally(() => setCheckingPrescriptionDraft(false));
    }, [appointment.id]);

    useEffect(() => {
        setCheckingSurvey(true);
        appointmentsApi
            .getQuestionnaireEntry(appointment.id.toString())
            .then(() => setSurveyRequired(false))
            .catch(error => {
                if (error.response?.status === 404) {
                    setSurveyRequired(true);
                }
            })
            .finally(() => setCheckingSurvey(false));
    }, [appointment.id]);

    const fetchIdentityConfirmationStatus = () => {
        setCheckingIdentityConfirmation(true);
        return patientProvider
            .getPatientPersonalDataStatus()
            .then(response => setIdentityConfirmed(response.isIdentityConfirmed))
            .catch(() => setIdentityConfirmed(false))
            .finally(() => setCheckingIdentityConfirmation(false));
    }

    const saveChangesHandler = async () => {
        if (stringValuesDiffers(appointment.patientReasons, reasonsInputValue)) {
            setLoading(true);
            await dispatch(
                setAdditionalPatientDetails({
                    appointmentId: appointment.id,
                    patientReasons: refineString(reasonsInputValue),
                }),
            );
            await dispatch(
                fetchAppointmentDetails({
                    appointmentId: appointment.id,
                    fetchSilently: true,
                }),
            );
            setLoading(false);
        }
    };

    const onIdentityConfirmationSuccess = () => {
        fetchIdentityConfirmationStatus().then(() => {
            openAlert({
            severity: 'success',
            message: t('identityConfirmationSuccess'),
            })
        })
        
    }


    if (checkingSurvey || checkingPrescriptionDraft || checkingIdentityConfirmation) {
        return <Loading loading={true} withBackdrop />;
    }

    if(!identityConfirmed)
    {
        return (
            <IdentityConfirmationPrompt onSuccess={() => onIdentityConfirmationSuccess()} />
        )
    }

    if(isPrescriptionAppointment && !prescriptionDraftExists)
    {
        return (
            <PatientPrescriptionFormPrompt appointmentId={appointment.id.toString()} />
        )
    }

    if (surveyRequired) {
        return (
            <>
                <PatientAppointmentSurveyPrompt
                    appointmentId={appointment.id.toString()}
                    isPrescriptionAppointment={isPrescriptionAppointment}
                />
            </>
        );
    }

    return (
        <>
            <h4 className={classesCommon.detailsHeader}>
                {t('detailsForAppointment')}
            </h4>
            <label
                className={classes.conditionsInputLabel}
                htmlFor="conditions-input"
            >
                {t('whatAreTheReasons')}?
            </label>
            <TextField
                id="conditions-input"
                variant="outlined"
                fullWidth
                defaultValue={
                    appointment.patientReasons ? appointment.patientReasons : ''
                }
                onChange={event => setReasonsInputValue(event.target.value)}
            />
            <Box className={classes.dropzone}>
                <Box style={{display: 'flex', alignItems: 'center'}}>
                            {t('attachments')}

                </Box>
                <Typography variant={'body2'} style={{
                    marginTop: 4,
                    marginBottom: 8,
                    color: GRAY,
                }}>{t('attachments.description')}</Typography>
                <Dropzone appointmentId={appointment.id} />
            </Box>
            <Box>
                <Button
                    className={classes.saveBtn}
                    variant={'contained'}
                    color={'secondary'}
                    onClick={saveChangesHandler}
                    disabled={saveChangesBtnDisabled}
                >
                    {t('saveChanges').toUpperCase()}
                </Button>
            </Box>
            <Loading loading={loading} withBackdrop />
        </>
    );
};

export default AdditionalDetailsEditable;
